import React, { useMemo, useState } from "react";
import { Avatar, Box, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  useGetCompanyUsersQuery, useCreateCompanyUserMutation,
  useUpdateCompanyUserMutation, useDeleteCompanyUserMutation, useGetRolesQuery,
} from "../../../Api/platformApi";
import {
  AppPage, PageHeader, FilterBar, SearchBox, DataTable, StatusBadge,
  ActionButtons, ActionButton, SlidePanel, ConfirmDialog, type DataTableColumn,
} from "../../../Components/ui";
import { NAVY } from "../../../theme/tokens";

const empty = { name: "", username: "", email: "", password: "", role_id: "", status: "active" };

const Users: React.FC = () => {
  const { data, isLoading } = useGetCompanyUsersQuery();
  const { data: rolesData } = useGetRolesQuery();
  const [createUser] = useCreateCompanyUserMutation();
  const [updateUser] = useUpdateCompanyUserMutation();
  const [deleteUser] = useDeleteCompanyUserMutation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(empty);
  const [search, setSearch] = useState("");
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const rows = data?.data ?? [];
  const roles = rolesData?.data ?? [];
  const set = (k: string) => (e: any) => setForm({ ...form, [k]: e.target.value });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((m: any) =>
      [m.user?.name, m.user?.username, m.user?.email]
        .filter(Boolean).some((v: string) => v.toLowerCase().includes(q)));
  }, [rows, search]);

  const save = async () => {
    await createUser({ ...form, role_id: form.role_id || null }).unwrap();
    setOpen(false);
    setForm(empty);
  };

  const changeRole = async (membershipId: number, role_id: any) => {
    await updateUser({ id: membershipId, body: { role_id: role_id || null } });
  };

  const columns: DataTableColumn<any>[] = [
    {
      id: "name", label: "User",
      render: (m) => (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            sx={{ width: 36, height: 36, bgcolor: "rgba(11,30,45,0.08)", color: NAVY, fontWeight: 700, fontSize: 14 }}
          >
            {(m.user?.name ?? "?").charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography fontSize={14} fontWeight={700} color={NAVY}>{m.user?.name}</Typography>
            <Typography fontSize={12} color="text.secondary">{m.user?.email}</Typography>
          </Box>
        </Stack>
      ),
    },
    { id: "username", label: "Username", render: (m) => m.user?.username || "—" },
    {
      id: "role", label: "Role",
      render: (m) => (
        <TextField
          select
          size="small"
          value={m.role_id ?? ""}
          sx={{ minWidth: 150 }}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => changeRole(m.id, e.target.value)}
        >
          <MenuItem value="">— none —</MenuItem>
          {roles.map((r: any) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
        </TextField>
      ),
    },
    { id: "status", label: "Status", render: (m) => <StatusBadge status={m.status} /> },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<PeopleRoundedIcon />}
          title="Users"
          subtitle="Team members with access to this company"
          actions={
            <ActionButton startIcon={<AddRoundedIcon />} onClick={() => { setForm(empty); setOpen(true); }}>
              Add User
            </ActionButton>
          }
        />
      }
    >
      <FilterBar right={<Typography fontSize={13} color="text.secondary">{filtered.length} users</Typography>}>
        <SearchBox value={search} onChange={setSearch} placeholder="Search users…" />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        actions={(m) => (
          <ActionButtons
            onDelete={() => setConfirmId(m.id)}
          />
        )}
        empty={{
          icon: <PeopleRoundedIcon />,
          title: "No users yet",
          message: "Add team members to grant them access to this company.",
          action: <ActionButton startIcon={<AddRoundedIcon />} onClick={() => { setForm(empty); setOpen(true); }}>Add User</ActionButton>,
        }}
      />

      <SlidePanel
        open={open}
        onClose={() => setOpen(false)}
        title="Add User"
        subtitle="Create a new team member"
        icon={<PeopleRoundedIcon />}
        footer={
          <>
            <Button variant="outlined" onClick={() => setOpen(false)} sx={{ borderRadius: "10px" }}>Cancel</Button>
            <ActionButton onClick={save} disabled={!form.name || !form.username || !form.password}>
              Save User
            </ActionButton>
          </>
        }
      >
        <Stack spacing={2}>
          <TextField label="Name" value={form.name} onChange={set("name")} fullWidth required />
          <TextField label="Username" value={form.username} onChange={set("username")} fullWidth required />
          <TextField label="Email" value={form.email} onChange={set("email")} fullWidth />
          <TextField label="Password" type="password" value={form.password} onChange={set("password")} fullWidth required />
          <TextField select label="Role" value={form.role_id} onChange={set("role_id")} fullWidth>
            <MenuItem value="">— none —</MenuItem>
            {roles.map((r: any) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
          </TextField>
        </Stack>
      </SlidePanel>

      <ConfirmDialog
        open={confirmId !== null}
        title="Remove member?"
        message="This will revoke the user's access to this company. The user account itself is not deleted."
        confirmText="Remove"
        danger
        onClose={() => setConfirmId(null)}
        onConfirm={async () => { if (confirmId !== null) await deleteUser(confirmId); setConfirmId(null); }}
      />
    </AppPage>
  );
};

export default Users;
